import { HttpClient } from '@angular/common/http';
/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import { RestResponse } from 'src/app/model';
import { CustomQuestion } from 'src/app/model/bvq/custom.question';
import { SearchableProvider } from 'src/app/model/interface';
import { Naics } from 'src/app/model/naics';
import { environment } from 'src/environments/environment';
import { ApiHeaders } from '../headers';

@Injectable()
export class NaicsProvider implements SearchableProvider {

  constructor(
    private http: HttpClient
  ) {}

  getNaicsList(page: number, size: number, q: string): Observable<RestResponse> {
    return this.http.
      get<RestResponse>(`${environment.API_URL}/api/bvq/naics?page=${page}&size=${size}` +
        (q ? '&q=' + encodeURIComponent(q) : ''),
      ApiHeaders.getOptions());
  }

  getFilteredNaics(page: number, size: number, query?: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/bvq/naics?page=${page}&size=${size}` +
        (query ? '&q=' + encodeURIComponent(query) : ''),
      ApiHeaders.getOptions());
  }

  getById(id: number): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(environment.API_URL + '/api/bvq/naics/' + id, ApiHeaders.getOptions());
  }

  getByIdWithMatrix(id: number): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(environment.API_URL + '/api/bvq/naics/' + id + '/matrix', ApiHeaders.getOptions());
  }

  getByIdWithMatrixFull(id: number): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(environment.API_URL + '/api/bvq/naics/' + id + '/full-matrix', ApiHeaders.getOptions());
  }

  save(id: string, field: string, value: any): Observable<RestResponse> {
    return undefined;
  }

  saveNaics(naics: Naics ): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(environment.API_URL + '/api/bvq/naics', naics, ApiHeaders.getOptions());
  }

  deleteNaics(naics: Naics): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(environment.API_URL + '/api/bvq/naics/' + naics.CODE + '/delete', null, ApiHeaders.getOptions());
  }

  getNaicsLimited(): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(environment.API_URL + '/api/bvq/naics/limited', ApiHeaders.getOptions());
  }

  sectionVisibility(naicsCode: number, field: string, value: boolean): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/naics/${naicsCode}/section?field=${field}&value=${value}`,
        null, ApiHeaders.getOptions());
  }

  search(query?: string, id?: string, nonBank?: boolean, lsp?: boolean, page?: number): Observable<RestResponse> {
    return this.getNaicsList(page ? page : 1, 25, query);
  }

  saveData(id: string, field: string, value: any): Observable<RestResponse> {
    return null;
  }

  /** Custom Manipulation */
  customUpdate(naicsCode: number, custom: CustomQuestion): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/naics/${naicsCode}/custom`, custom, ApiHeaders.getOptions());
  }

  customDelete(naicsCode: number, customId: number): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/naics/${naicsCode}/custom/${customId}`, null, ApiHeaders.getOptions());
  }

  getSearchType(): string {
    return 'NAICS';
  }

  getUpdatedDate(naics: number): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(environment.API_URL + '/api/cross-server/industry/updated/' + naics, ApiHeaders.getOptions());
  }
}
